import styled, { css } from "styled-components";

import { colorsPalettes, setElevation } from "@similarweb/styles";
import { Flex, Stack } from "@similarweb/ui-components/foundations";

import {
    genieCloseAnimation,
    genieOpenAnimation,
    PANEL_ANIMATION_DURATION_STRING,
    PANEL_ANIMATION_EASING,
} from "app/pages/sales-intelligence/common-components/animations";

const EXPANDED_PANEL_WIDTH = 696;

export const StyledExpandedContainer = styled(Stack)<{
    $isOpen?: boolean;
    $isAnimated?: boolean;
    $transformOrigin?: string;
}>`
    position: absolute;
    top: 0;
    left: 0;
    width: ${EXPANDED_PANEL_WIDTH}px;
    height: 100%;
    max-height: 100%;
    z-index: 25;
    isolation: isolate;
    min-height: 0;
    overflow: hidden;

    ${setElevation("Z1_hover")};
    border: 1px solid ${colorsPalettes.bluegrey[100]};
    border-radius: 8px 16px 16px 8px;
    background: ${colorsPalettes.carbon[0]};
    box-sizing: border-box;

    pointer-events: ${({ $isOpen, $isAnimated }) => ($isOpen || $isAnimated ? "auto" : "none")};
    visibility: ${({ $isOpen, $isAnimated }) => ($isOpen || $isAnimated ? "visible" : "hidden")};

    /* genie emerges from / collapses into the clicked button */
    transform-origin: ${({ $transformOrigin }) => $transformOrigin ?? "bottom left"};

    ${({ $isAnimated, $isOpen }) => {
        const restState = css`
            opacity: ${$isOpen ? 1 : 0};
            transform: ${$isOpen ? "scale(1)" : "scale(0.1)"};
        `;

        if (!$isAnimated) {
            return restState;
        }

        return css`
            will-change: transform, opacity;
            /* "both": hold the first frame before start AND the last frame after end, so the
               closed overlay stays invisible until it unmounts (no revert-to-visible blink). */
            animation: ${$isOpen ? genieOpenAnimation : genieCloseAnimation} ${PANEL_ANIMATION_DURATION_STRING}
                ${PANEL_ANIMATION_EASING} both;

            @media (prefers-reduced-motion: reduce) {
                animation: none;
                ${restState};
            }
        `;
    }};
`;

export const StyledOverlayHeader = styled(Flex)`
    align-items: flex-start;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid ${colorsPalettes.carbon[100]};
`;

// Design spec requires 13/16 — no matching setFontVariant preset exists
export const StyledOverlaySubtitle = styled.p`
    margin: 0;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
    color: ${colorsPalettes.carbon[400]};
`;

export const StyledOverlayContent = styled.div`
    flex-shrink: 1;
    overflow-y: auto;
    min-height: 0;
    height: 100%;
`;
